<template>
    <div>
        <b-modal
            ref="product-category-modal"
            cancel-variant="outline-secondary"
            size="xl"
            :ok-title="$t('Component(Button):close')"
            ok-only
            centered
            :title="$t('Component(CategorySelector):title')"
        >
            <template #modal-header="{ close }">
                <h5>{{ title }}</h5>
                <!-- Emulate built in modal header close button action -->
                <Button size="sm" variant="primary" @click="close()">
                    {{ $t('Component(ProductCategorySelectModal):header-close-button') }}
                </Button>
            </template>

<!--            <b-input-group class="input-group-merge mb-1">-->
<!--                <b-input-group-prepend is-text>-->
<!--                    <feather-icon icon="SearchIcon" />-->
<!--                </b-input-group-prepend>-->
<!--                <b-form-input @input="onSearch" placeholder="Search" />-->
<!--            </b-input-group>-->
            <ProductCategoryItem
                ref="categories"
                :selected="selected"
                :visible="true"
                :only-selectable="onlySelectable"
                :searchString="searchString"
                @change="onCategoryChange"
                v-for="node in options" :node="node" :parent="node" />
        </b-modal>
        <b-card no-body :border-variant="border ? borderVariant : ''">
            <div class="card-header">
                <div class="col-md-6 pl-0">
                    <b-card-title id="popover-button-1">{{ title }}</b-card-title>
                    <slot name="sub-title"/>
                </div>
                <div class="col-md-6 pr-0" style="display: flex;">
                    <b-form-group class="mb-0 mr-1 w-100">
                        <vue-autosuggest
                            ref="autocomplete"
                            :suggestions="suggestions"
                            :input-props="inputProps"
                            :get-suggestion-value="getSuggestionValue"
                            @selected="onSuggestionSelect"
                            @input="fetchResults"
                        >
                            <div slot-scope="{suggestion}">
                                <span class="pr-1">
                                    <feather-icon class="text-primary" icon="CheckIcon" v-if="isSelected(suggestion)" />
                                </span>
                                <span v-html="suggestion.item.highlightedTitle" />
                            </div>
                        </vue-autosuggest>
                    </b-form-group>
                    <b-button
                        v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                        variant="primary"
                        class="btn-icon"
                        @click="showModal"
                    >
                        {{ $t('Component(ProductCategorySelect):browse') }}
                    </b-button>
                </div>
            </div>

            <b-alert
                variant="light"
                :show="showInstruction"
                class="ml-2 mr-2 mb-2"
            >
                <div class="alert-body">
                    {{ $t('Component(ProductCategory):instruction') }}
                    <br>
                    <a class="float-right" href="https://taxation-customs.ec.europa.eu/customs-4/calculation-customs-duties/customs-tariff/eu-customs-tariff-taric_en" target="_blank">{{ $t('Component(Link):more-information') }}</a>
                </div>
            </b-alert>

            <Transition mode="out-in" name="fade">
                <div>
                    <b-alert
                        variant="secondary"
                        show
                        class="m-2"
                        v-if="items.length === 0"
                    >
                        <div class="alert-body">
                            <span>{{ $t('Component(ProductCategory):empty-description') }}</span>
                        </div>
                    </b-alert>
                    <b-table
                        :fields="fields"
                        :items="items"
                        v-if="selected.length"
                        responsive="sm"
                    >
                        <template #cell(hierarchy)="data">
                            <div v-if="!hasParentHierarchy(data.item)">
                                <b-spinner small />
                            </div>
                            <div v-if="hasParentHierarchy(data.item)" v-for="title in getHierarchyTitle(data.item)">
                                <feather-icon icon="ChevronRightIcon" /> {{ title }}
                            </div>
                        </template>

                        <!-- A custom formatted column -->
                        <template #cell(title)="data">
                            {{ data.item.node.title }}
                        </template>

                        <template #cell(actions)="data">
                            <div class="text-right">
                                <b-button
                                    v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                                    variant="danger"
                                    class="btn-icon rounded-circle"
                                    @click="removeItem(data.item)"
                                >
                                    <feather-icon icon="Trash2Icon" />
                                </b-button>
                            </div>
                        </template>

                        <!-- Optional default data cell scoped slot -->
                        <template #cell()="data">
                            {{ data.value }}
                        </template>
                    </b-table>
                </div>

            </Transition>
        </b-card>
    </div>
</template>
<script>
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import {Button} from "@core/components/common";
import {
    BFormGroup, BListGroup, BListGroupItem, BTable, BCard, BCardTitle, BSpinner,
    BButton, BInputGroup, BFormInput, BInputGroupPrepend, BFormCheckbox, BAlert
} from 'bootstrap-vue'
import {ProductApi} from '@core/api'
import Ripple from 'vue-ripple-directive'
import ProductCategoryItem from "@core/components/product/ProductCategoryItem";
import { VueAutosuggest } from 'vue-autosuggest'

export default {
    components: {
        BFormGroup, BListGroup, BListGroupItem,
        BTable, BCard, BButton, BCardTitle, BInputGroup, BFormInput, BInputGroupPrepend, BFormCheckbox, BAlert,
        BSpinner, ProductCategoryItem, AppCollapse, VueAutosuggest, Button,
    },
    directives: {
        Ripple,
    },

    props: {
        title: {
            type: String,
            default() {
                return this.$t('Component(ProductCategorySelect):title')
            }
        },
        border: {
            type: Boolean,
            default: false
        },
        borderVariant: {
            default: 'light'
        },
        selectedItems: {
            type: Array,
            default() {
                return [];
            }
        },
        hideHierarchyColumn: {
            default: false
        },
        showInstruction: {
            default: true
        },
        onlySelectable: {
            default: true
        }
    },

    data() {
        const fields = [
            { key: 'hierarchy', label: this.$t('Component(ProductCategorySelect):column-hierarchy') },
            { key: 'title', label: this.$t('Component(ProductCategorySelect):column-title') },
            { key: 'actions', label: this.$t('Component(ProductCategorySelect):column-actions'), class: 'text-right' }
        ];

        if (this.hideHierarchyColumn) {
            fields.splice(0, 1);
        }

        return {
            selected: [],
            items: [],
            buffer: {},
            options: [],
            tId: null,
            searchString: '',
            isSearchStarted: false,
            inputProps: {
                id: 'autosuggest__input_ajax',
                placeholder: this.$t('Component(ProductCategorySelect):search-placeholder'),
                class: 'form-control w-100',
                name: 'ajax',
            },
            suggestions: [],
            searchTimer: null,
            fields: fields,
        };
    },

    methods: {
        onSearch(qs) {
            if (this.tId) {
                clearTimeout(this.tId);
            }
            this.tId = setTimeout(() => {
                this.searchString = qs.toLowerCase();
                this.$refs.categories.forEach(n => n.search(qs.toLowerCase()));
            }, 600);
        },
        getHierarchyTitle(item) {
            if (!this.hasParentHierarchy(item)) {
                return [];
            }

            if (typeof item.category === 'object') {

                if (typeof item.category.getHierarchy === 'function') {
                    return item.category.getHierarchy().map((n) => n.title);
                }

                return item.category.hierarchy.map((n) => n.title);
            }
            return item.node.getHierarchy().map((n) => n.title);
        },
        hasParentHierarchy(item) {
            if (typeof item.category === 'object' && item.category.hierarchy) {
                return true;
            }
            return typeof item.node.getHierarchy === 'function';
        },
        onCategoryChange({node, parent, selected}) {
            let item = {
                category: node,
                node: node,
                parent: parent,
            };
            if (!selected) {
                this.removeItem(item);
                return;
            }

            this.addItem(item);
            this.emitChange();
        },
        showModal() {
            this.$refs['product-category-modal'].show();
        },
        onSave() {

        },
        removeItem(item, emitChanges = true) {
            let index = this.selected.findIndex((element) => element === item.node.id);
            if (index > -1) {
                this.selected.splice(index, 1);
            }

            index = this.items.findIndex((i) => i.node.id === item.node.id);
            if (index > -1) {
                this.items.splice(index, 1);
            }

            if (emitChanges) {
                this.emitChange();
            }
        },
        addItem(item) {
            if (this.hasItem(item)) {
                return
            }

            this.selected.push(item.node.id);
            this.items.push(item);
        },
        hasItem(item) {
            return this.selected.includes(item.node.id);
        },
        emitChange() {
            this.$emit('change', {
                idList: this.selected,
                items: this.items,
            });
        },
        fetchResults(qs) {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }
            this.searchTimer = setTimeout(() => {
                if ('' === qs) {
                    this.suggestions = [{data: []}];
                } else {
                    ProductApi.getCategories(qs).then((r) => {
                        this.suggestions = [{
                            data: this.highlightQueryString(qs, r.data),
                        }];
                    });
                }

            }, 600);
        },
        highlightQueryString(qs, items) {
            if ('' === qs) {
                return items;
            }

            items.forEach((item) => {
                item.highlightedTitle = item.title.replace(new RegExp('(' + qs + ')', 'i'), "<b class='search-qs'>$1</b>");
            });

            return items;
        },
        getSuggestionValue() {
            return '';
        },
        onSuggestionSelect(suggestion) {
            this.addItem({
                category: suggestion.item,
                node: suggestion.item
            });

            ProductApi.getCategory(suggestion.item.id).then((r) => {
                let item = this.items.find(item => item.node.id === suggestion.item.id)

                if (item) {
                    item.category = r.data;
                    this.removeItem(item, false);
                    this.addItem(item);
                    this.emitChange();
                }
            });

            this.suggestions = [{data: []}];
        },
        fetchCategory(id) {
            let item = this.items.find(item => item.node.id === id)
            ProductApi.getCategory(id).then((r) => {
                if (item) {
                    item.category = r.data;
                    this.removeItem(item, false);
                    this.addItem(item);
                    this.emitChange();
                }
            });
        },
        isSelected(suggestion) {
            return this.hasItem({node: suggestion.item});
        },
        initItems(value) {
            value.forEach(v => {
                this.addItem({node: v, category: v});
                if (!v.hierarchy) {
                    this.fetchCategory(v.id);
                }
            });
        }
    },

    watch: {
        selectedItems(value) {
            this.lastInitedValue = value;
            this.items = [];
            this.selected = [];
            this.initItems(value);
        }
    },

    created() {
        ProductApi.getCategories().then(r => this.options = r.data);

        if (this.selectedItems.length) {
            this.initItems(this.selectedItems);
        }
    }
};
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.autosuggest__results-item {
    cursor: pointer;
}
</style>
